import { useEffect, useRef, useCallback, useState } from 'react';

const TURNSTILE_SITE_KEY = '0x4AAAAAAC1IbAz4RRCjXLBN';

const Turnstile = ({ onVerify, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Load Turnstile script
  useEffect(() => {
    if (window.turnstile) {
      setScriptLoaded(true);
      return;
    }

    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      existing.addEventListener('load', () => setScriptLoaded(true));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  const handleVerify = useCallback((token) => {
    onVerify?.(token);
  }, [onVerify]);

  // Render widget
  useEffect(() => {
    if (!scriptLoaded || !window.turnstile || !containerRef.current) return;

    // Clean up previous widget
    if (widgetIdRef.current !== null) {
      try { window.turnstile.remove(widgetIdRef.current); } catch {}
      widgetIdRef.current = null;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: handleVerify,
      'expired-callback': () => onExpire?.(),
      theme: 'light',
      size: 'flexible',
    });

    return () => {
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [scriptLoaded, handleVerify, onExpire]);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
};

export default Turnstile;
